import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import axios from "../../utils/api.js";

const StarHotel = () => {
  const [formData, setFormData] = useState({
    basicHotelInfo: {
      hotelName: "",
      hotelType: "",
      hotelChain: "",
      totalFloorArea: 0,
      totalFloors: 0,
      totalRooms: 0,
      totalSuites: 0,
      accreditations: [], // Array of strings
    },
    dataCollectionDetails: {
      dateOfDataCollection: "",
      dataCollectedBy: "",
      dataSource: "",
    },
    hotelInfrastructureGuestFlow: {
      standardRooms: 0,
      standardRoomCost: 0,
      standardRoomOccupancy: 0,
      deluxeRooms: 0,
      deluxeRoomCost: 0,
      deluxeRoomOccupancy: 0,
      suites: 0,
      suiteCost: 0,
      suiteOccupancy: 0,
      banquetHallCount: 0,
      banquetHallUsageRate: 0,
      avgDailyGuests: 0,
      restaurantCount: 0,
      restaurantFootfall: 0,
      kitchenCleaningFrequency: 0,
    },
    hotelHygieneDisinfectionPractices: {
      cleaningDisinfectionBudget: 0,
      cleaningCostDistributionrooms: 0,
      cleaningCostDistributionbanquetHalls: 0,
      cleaningCostDistributionrestaurants: 0,
      cleaningCostDistributionspaGym: 0,
      cleaningCostDistributionlobbyCommonAreas: 0,
      cleaningCostDistributionkitchen: 0,
      disinfectionTechniques: [],
      avgDisinfectionTime: 0,
      disinfectionStaffAvailability: "",
      cleaningChallenges: [],
    },
    guestHealthSafetyCompliance: {
      healthSafetyCertified: false,
      disinfectionFrequencyPerRoom: 0,
      disinfectionProtocols: [],
      hygieneAuditFrequency: 0,
      guestHygieneComplaints: {
        hasComplaints: false,
        details: "",
      },
    },
    staffExposureAutomationReadiness: {
      housekeepingStaffPerShift: 0,
      avgRoomCleaningTime: 0,
      staffDisinfectionComplaints: 0,
      chemicalExposureReports: 0,
      currentPPEUsage: [],
      automationReadiness: false,
    },
    timeCostEfficiencyUVCRobot: {
      manualDisinfectionTimePerDay: 0,
      highRiskAreaDisinfectionTime: 0,
      chemicalDisinfectionDryTime: 0,
      labourCostPerMonth: 0,
      disinfectionChemicalCost: 0,
      electricityConsumption: 0,
      guestRefundCost: 0,
    },
    aiUVCRobotFeasibility: {
      interestInAI: false,
      estimatedBudget: 0,
      financialModel: "",
      customizationNeeds: [],
      integrationNeeds: [],
      safetyConcerns: [],
    },
    requiredDemonstrationNextSteps: {
      onsiteDemoInterest: false,
      pilotProjectReadiness: false,
      keyDecisionMaker: {
        name: "",
        contact: "",
      },
      decisionMakingTimeline: "",
    },
    additionalnotes: {
      note: "",
    },
  });
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const FORM_STEPS = [
    "Basic Hotel Info",
    "Data Collection Details",
    "Hotel Infrastructure & Guest Flow",
    "Hotel Hygiene & Disinfection Practices",
    "Guest Health & Safety Compliance",
    "Staff Exposure & Automation Readiness",
    "Time & Cost Efficiency of UVC Robot",
    "AI/UVC Robot Feasibility",
    "Required Demonstration & Next Steps",
    "Additional Notes",
  ];
  const totalSteps = FORM_STEPS.length;

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };
  const cardStyle = {
    maxWidth: 720,
    margin: "0 auto 24px",
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 28,
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  };
  const inputGroupStyle = { marginBottom: 18 };
  const labelStyle = {
    display: "block",
    marginBottom: 6,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#64748b",
  };
  const inputStyle = {
    width: "100%",
    padding: "0.65rem 0.85rem",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    fontSize: "1rem",
    outline: "none",
    boxSizing: "border-box",
  };
  const sectionTitleStyle = {
    margin: "0 0 20px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#1e293b",
  };

  const handleChange = (e, section, nestedSection) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === "number") {
      newValue = Math.max(0, Number(value)) || 0;
    } else if (type === "checkbox") {
      newValue = e.target.checked; // Convert to boolean
    }

    if (nestedSection) {
      setFormData((prevData) => ({
        ...prevData,
        [section]: {
          ...prevData[section],
          [nestedSection]: {
            ...prevData[section][nestedSection],
            [name]: newValue,
          },
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [section]: {
          ...prevData[section],
          [name]: newValue,
        },
      }));
    }
  };

  const handleAccreditationChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      basicHotelInfo: {
        ...prevData.basicHotelInfo,
        accreditations: value.split(",").map((item) => item.trim()), // Convert comma-separated string to array
      },
    }));
  };

  const handleArrayChange = (e, section, field) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value.split(",").map((item) => item.trim()),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post("/post-star-hotels", formData, {
        headers: {
          Authorization: `Bearer ${token}`, // Add auth token
          "Content-Type": "application/json",
        },
      });

      console.log("Data posted successfully:", response.data);
      alert("Hotel data submitted successfully!");
      navigate("/Hoteldata");
      // Reset form after successful submission
      setFormData({
        basicHotelInfo: {
          hotelName: "",
          hotelType: "",
          hotelChain: "",
          totalFloorArea: 0,
          totalFloors: 0,
          totalRooms: 0,
          totalSuites: 0,
          accreditations: [],
        },
        dataCollectionDetails: {
          dateOfDataCollection: "",
          dataCollectedBy: "",
          dataSource: "",
        },
        hotelInfrastructureGuestFlow: {
          standardRooms: 0,
          standardRoomCost: 0,
          standardRoomOccupancy: 0,
          deluxeRooms: 0,
          deluxeRoomCost: 0,
          deluxeRoomOccupancy: 0,
          suites: 0,
          suiteCost: 0,
          suiteOccupancy: 0,
          banquetHallCount: 0,
          banquetHallUsageRate: 0,
          avgDailyGuests: 0,
          restaurantCount: 0,
          restaurantFootfall: 0,
          kitchenCleaningFrequency: 0,
        },
        hotelHygieneDisinfectionPractices: {
          cleaningDisinfectionBudget: 0,
          cleaningCostDistributionrooms: 0,
          cleaningCostDistributionbanquetHalls: 0,
          cleaningCostDistributionrestaurants: 0,
          cleaningCostDistributionspaGym: 0,
          cleaningCostDistributionlobbyCommonAreas: 0,
          cleaningCostDistributionkitchen: 0,
          disinfectionTechniques: [],
          avgDisinfectionTime: 0,
          disinfectionStaffAvailability: "",
          cleaningChallenges: [],
        },
        guestHealthSafetyCompliance: {
          healthSafetyCertified: false,
          disinfectionFrequencyPerRoom: 0,
          disinfectionProtocols: [],
          hygieneAuditFrequency: 0,
          guestHygieneComplaints: {
            hasComplaints: false,
            details: "",
          },
        },
        staffExposureAutomationReadiness: {
          housekeepingStaffPerShift: 0,
          avgRoomCleaningTime: 0,
          staffDisinfectionComplaints: 0,
          chemicalExposureReports: 0,
          currentPPEUsage: [],
          automationReadiness: false,
        },
        timeCostEfficiencyUVCRobot: {
          manualDisinfectionTimePerDay: 0,
          highRiskAreaDisinfectionTime: 0,
          chemicalDisinfectionDryTime: 0,
          labourCostPerMonth: 0,
          disinfectionChemicalCost: 0,
          electricityConsumption: 0,
          guestRefundCost: 0,
        },
        aiUVCRobotFeasibility: {
          interestInAI: false,
          estimatedBudget: 0,
          financialModel: "",
          customizationNeeds: [],
          integrationNeeds: [],
          safetyConcerns: [],
        },
        requiredDemonstrationNextSteps: {
          onsiteDemoInterest: false,
          pilotProjectReadiness: false,
          keyDecisionMaker: {
            name: "",
            contact: "",
          },
          decisionMakingTimeline: "",
        },
        additionalnotes: {
          note: "",
        },
      });
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message
      );
      alert(
        `Failed to submit hotel data: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Star Hotel Form
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Complete the sections below. Use Previous and Next to move between steps.
        </p>
      </div>

      <div
        style={{
          maxWidth: 720,
          margin: "0 auto 20px",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {FORM_STEPS.map((name, i) => (
          <span
            key={i}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: "0.8rem",
              fontWeight: 500,
              background:
                currentStep === i
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "#e2e8f0",
              color: currentStep === i ? "#fff" : "#64748b",
            }}
          >
            {name}
          </span>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <div style={cardStyle}>
          {currentStep === 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Basic Hotel Info</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 1 of {totalSteps}
                </p>
              </div>
          {[
            { label: "Hotel Name", name: "hotelName", type: "text" },
            {
              label: "Hotel Type",
              name: "hotelType",
              type: "select",
              options: ["3-Star", "4-Star", "5-Star", "Resort", "Business"],
            },
            { label: "Hotel Chain", name: "hotelChain", type: "text" },
            {
              label: "Total Floor Area (sq. ft.)",
              name: "totalFloorArea",
              type: "number",
            },
            { label: "Total Floors", name: "totalFloors", type: "number" },
            { label: "Total Rooms", name: "totalRooms", type: "number" },
            { label: "Total Suites", name: "totalSuites", type: "number" },
            {
              label: "Accreditations (comma-separated)",
              name: "accreditations",
              type: "text",
            },
          ].map(({ label, name, type, options }) => (
            <div style={inputGroupStyle} key={name}>
              <label style={labelStyle}>{label}</label>
              {type === "select" ? (
                <select
                  name={name}
                  value={formData.basicHotelInfo[name] || ""}
                  onChange={(e) => handleChange(e, "basicHotelInfo")}
                  style={inputStyle}
                >
                  <option value="">Select {label}</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={type}
                  name={name}
                  placeholder={
                    type === "number"
                      ? "Enter a number"
                      : "Enter " + label.toLowerCase()
                  }
                  value={
                    name === "accreditations"
                      ? formData.basicHotelInfo[name].join(", ") // Convert array to comma-separated string
                      : formData.basicHotelInfo[name] || ""
                  }
                  onChange={
                    name === "accreditations"
                      ? handleAccreditationChange
                      : (e) => {
                          const val =
                            type === "number"
                              ? Math.max(0, Number(e.target.value)) || 0
                              : e.target.value;
                          handleChange(
                            { target: { name, value: val } },
                            "basicHotelInfo"
                          );
                        }
                  }
                  style={inputStyle}
                />
              )}
            </div>
          ))}
            </>
          )}

          {currentStep === 1 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Data Collection Details</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 2 of {totalSteps}
                </p>
              </div>
          {/* Date of Data Collection */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date of Data Collection</label>
            <input
              type="date"
              name="dateOfDataCollection"
              value={formData.dataCollectionDetails.dateOfDataCollection || ""}
              onChange={(e) =>
                handleChange(
                  {
                    target: {
                      name: "dateOfDataCollection",
                      value: e.target.value,
                    },
                  },
                  "dataCollectionDetails"
                )
              }
              style={inputStyle}
            />
          </div>

          {/* Data Collected By */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Data Collected By</label>
            <input
              type="text"
              name="dataCollectedBy"
              placeholder="Enter the name of the data collector"
              value={formData.dataCollectionDetails.dataCollectedBy || ""}
              onChange={(e) =>
                handleChange(
                  {
                    target: { name: "dataCollectedBy", value: e.target.value },
                  },
                  "dataCollectionDetails"
                )
              }
              style={inputStyle}
            />
          </div>

          {/* Data Source */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Data Source</label>
            <select
              name="dataSource"
              value={formData.dataCollectionDetails.dataSource || ""}
              onChange={(e) =>
                handleChange(
                  { target: { name: "dataSource", value: e.target.value } },
                  "dataCollectionDetails"
                )
              }
              style={inputStyle}
            >
              <option value="">Select Data Source</option>
              <option value="Facility Management">Facility Management</option>
              <option value="Housekeeping">Housekeeping</option>
              <option value="Procurement">Procurement</option>
            </select>
          </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Hotel Infrastructure & Guest Flow</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 3 of {totalSteps}
                </p>
              </div>
          {/* Standard Rooms */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Standard Rooms</label>
            <input
              type="number"
              name="standardRooms"
              placeholder="Enter number of standard rooms"
              value={formData.hotelInfrastructureGuestFlow.standardRooms || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "standardRooms", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Standard Room Cost (per night)</label>
            <input
              type="number"
              name="standardRoomCost"
              placeholder="Enter cost of standard rooms"
              value={
                formData.hotelInfrastructureGuestFlow.standardRoomCost || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: { name: "standardRoomCost", value },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Standard Room Occupancy (%)</label>
            <input
              type="number"
              name="standardRoomOccupancy"
              placeholder="Enter occupancy rate for standard rooms"
              value={
                formData.hotelInfrastructureGuestFlow.standardRoomOccupancy ||
                ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "standardRoomOccupancy",
                      value,
                    },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Deluxe Rooms */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Deluxe Rooms</label>
            <input
              type="number"
              name="deluxeRooms"
              placeholder="Enter number of deluxe rooms"
              value={formData.hotelInfrastructureGuestFlow.deluxeRooms || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "deluxeRooms", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Deluxe Room Cost (per night)</label>
            <input
              type="number"
              name="deluxeRoomCost"
              placeholder="Enter cost of deluxe rooms"
              value={formData.hotelInfrastructureGuestFlow.deluxeRoomCost || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "deluxeRoomCost", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Deluxe Room Occupancy (%)</label>
            <input
              type="number"
              name="deluxeRoomOccupancy"
              placeholder="Enter occupancy rate for deluxe rooms"
              value={
                formData.hotelInfrastructureGuestFlow.deluxeRoomOccupancy || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "deluxeRoomOccupancy",
                      value,
                    },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Suites */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Suites</label>
            <input
              type="number"
              name="suites"
              placeholder="Enter number of suites"
              value={formData.hotelInfrastructureGuestFlow.suites || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "suites", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Suite Cost (per night)</label>
            <input
              type="number"
              name="suiteCost"
              placeholder="Enter cost of suites"
              value={formData.hotelInfrastructureGuestFlow.suiteCost || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "suiteCost", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Suite Occupancy (%)</label>
            <input
              type="number"
              name="suiteOccupancy"
              placeholder="Enter occupancy rate for suites"
              value={formData.hotelInfrastructureGuestFlow.suiteOccupancy || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "suiteOccupancy", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Banquet Halls */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Banquet Hall Count</label>
            <input
              type="number"
              name="banquetHallCount"
              placeholder="Enter number of banquet halls"
              value={
                formData.hotelInfrastructureGuestFlow.banquetHallCount || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: { name: "banquetHallCount", value },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Banquet Hall Usage Rate (%)</label>
            <input
              type="number"
              name="banquetHallUsageRate"
              placeholder="Enter usage rate for banquet halls"
              value={
                formData.hotelInfrastructureGuestFlow.banquetHallUsageRate || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "banquetHallUsageRate",
                      value,
                    },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Guest Flow */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Average Daily Guests</label>
            <input
              type="number"
              name="avgDailyGuests"
              placeholder="Enter average daily guests"
              value={formData.hotelInfrastructureGuestFlow.avgDailyGuests || ""}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  { target: { name: "avgDailyGuests", value } },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Restaurants */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Restaurant Count</label>
            <input
              type="number"
              name="restaurantCount"
              placeholder="Enter number of restaurants"
              value={
                formData.hotelInfrastructureGuestFlow.restaurantCount || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: { name: "restaurantCount", value },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Restaurant Footfall (daily)</label>
            <input
              type="number"
              name="restaurantFootfall"
              placeholder="Enter daily restaurant footfall"
              value={
                formData.hotelInfrastructureGuestFlow.restaurantFootfall || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "restaurantFootfall",
                      value,
                    },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Kitchen Cleaning */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Kitchen Cleaning Frequency (per day)
            </label>
            <input
              type="number"
              name="kitchenCleaningFrequency"
              placeholder="Enter kitchen cleaning frequency"
              value={
                formData.hotelInfrastructureGuestFlow
                  .kitchenCleaningFrequency || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "kitchenCleaningFrequency",
                      value,
                    },
                  },
                  "hotelInfrastructureGuestFlow"
                );
              }}
              style={inputStyle}
            />
          </div>
            </>
          )}

          {currentStep === 3 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Hotel Hygiene & Disinfection Practices</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 4 of {totalSteps}
                </p>
              </div>
          {/* Cleaning & Disinfection Budget */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Cleaning & Disinfection Budget</label>
            <input
              type="number"
              name="cleaningDisinfectionBudget"
              placeholder="Enter cleaning and disinfection budget"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningDisinfectionBudget || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningDisinfectionBudget",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Cleaning Cost Distribution */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Rooms (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionrooms"
              placeholder="Enter cost distribution for rooms"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionrooms || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionrooms",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Banquet Halls (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionbanquetHalls"
              placeholder="Enter cost distribution for banquet halls"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionbanquetHalls || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionbanquetHalls",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Restaurants (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionrestaurants"
              placeholder="Enter cost distribution for restaurants"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionrestaurants || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionrestaurants",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Spa & Gym (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionspaGym"
              placeholder="Enter cost distribution for spa & gym"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionspaGym || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionspaGym",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Lobby & Common Areas (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionlobbyCommonAreas"
              placeholder="Enter cost distribution for lobby & common areas"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionlobbyCommonAreas || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionlobbyCommonAreas",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Cost Distribution - Kitchen (%)
            </label>
            <input
              type="number"
              name="cleaningCostDistributionkitchen"
              placeholder="Enter cost distribution for kitchen"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .cleaningCostDistributionkitchen || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "cleaningCostDistributionkitchen",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Disinfection Techniques */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Disinfection Techniques (comma-separated)
            </label>
            <input
              type="text"
              name="disinfectionTechniques"
              placeholder="Enter disinfection techniques"
              value={
                formData.hotelHygieneDisinfectionPractices.disinfectionTechniques.join(
                  ", "
                ) || ""
              }
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  hotelHygieneDisinfectionPractices: {
                    ...prevData.hotelHygieneDisinfectionPractices,
                    disinfectionTechniques: e.target.value
                      .split(",")
                      .map((item) => item.trim()),
                  },
                }))
              }
              style={inputStyle}
            />
          </div>

          {/* Average Disinfection Time */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Average Disinfection Time (minutes)
            </label>
            <input
              type="number"
              name="avgDisinfectionTime"
              placeholder="Enter average disinfection time"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .avgDisinfectionTime || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "avgDisinfectionTime",
                      value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Disinfection Staff Availability */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Disinfection Staff Availability</label>
            <input
              type="text"
              name="disinfectionStaffAvailability"
              placeholder="Enter staff availability details"
              value={
                formData.hotelHygieneDisinfectionPractices
                  .disinfectionStaffAvailability || ""
              }
              onChange={(e) =>
                handleChange(
                  {
                    target: {
                      name: "disinfectionStaffAvailability",
                      value: e.target.value,
                    },
                  },
                  "hotelHygieneDisinfectionPractices"
                )
              }
              style={inputStyle}
            />
          </div>

          {/* Cleaning Challenges */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Cleaning Challenges (comma-separated)
            </label>
            <input
              type="text"
              name="cleaningChallenges"
              placeholder="Enter cleaning challenges"
              value={
                formData.hotelHygieneDisinfectionPractices.cleaningChallenges.join(
                  ", "
                ) || ""
              }
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  hotelHygieneDisinfectionPractices: {
                    ...prevData.hotelHygieneDisinfectionPractices,
                    cleaningChallenges: e.target.value
                      .split(",")
                      .map((item) => item.trim()),
                  },
                }))
              }
              style={inputStyle}
            />
          </div>
            </>
          )}

          {currentStep === 4 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Guest Health & Safety Compliance</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 5 of {totalSteps}
                </p>
              </div>
          {/* Health & Safety Certified */}
          <div style={inputGroupStyle}>
            {/* Health & Safety Certified (Radio Buttons) */}
            <label style={labelStyle}>Health & Safety Certified</label>
            <div style={{ display: "flex", gap: "15px" }}>
              {/* Yes Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="healthSafetyCertified"
                  value="true"
                  checked={
                    formData.guestHealthSafetyCompliance
                      .healthSafetyCertified === true
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: { name: "healthSafetyCertified", value: true },
                      },
                      "guestHealthSafetyCompliance"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "green" }}>Yes</span>
              </label>

              {/* No Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="healthSafetyCertified"
                  value="false"
                  checked={
                    formData.guestHealthSafetyCompliance
                      .healthSafetyCertified === false
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: { name: "healthSafetyCertified", value: false },
                      },
                      "guestHealthSafetyCompliance"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "red" }}>No</span>
              </label>
            </div>
          </div>

          {/* Disinfection Frequency Per Room */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Disinfection Frequency Per Room (per day)
            </label>
            <input
              type="number"
              name="disinfectionFrequencyPerRoom"
              placeholder="Enter disinfection frequency per room"
              value={
                formData.guestHealthSafetyCompliance
                  .disinfectionFrequencyPerRoom || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "disinfectionFrequencyPerRoom",
                      value,
                    },
                  },
                  "guestHealthSafetyCompliance"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Disinfection Protocols */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Disinfection Protocols (comma-separated)
            </label>
            <input
              type="text"
              name="disinfectionProtocols"
              placeholder="Enter disinfection protocols"
              value={
                formData.guestHealthSafetyCompliance.disinfectionProtocols.join(
                  ", "
                ) || ""
              }
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  guestHealthSafetyCompliance: {
                    ...prevData.guestHealthSafetyCompliance,
                    disinfectionProtocols: e.target.value
                      .split(",")
                      .map((item) => item.trim()),
                  },
                }))
              }
              style={inputStyle}
            />
          </div>

          {/* Hygiene Audit Frequency */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Hygiene Audit Frequency (per month)
            </label>
            <input
              type="number"
              name="hygieneAuditFrequency"
              placeholder="Enter hygiene audit frequency"
              value={
                formData.guestHealthSafetyCompliance.hygieneAuditFrequency || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "hygieneAuditFrequency",
                      value,
                    },
                  },
                  "guestHealthSafetyCompliance"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Guest Hygiene Complaints (Radio Buttons) */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Guest Hygiene Complaints</label>
            <div style={{ display: "flex", gap: "15px" }}>
              {/* Yes Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="guestHygieneComplaints"
                  value="true"
                  checked={
                    formData.guestHealthSafetyCompliance.guestHygieneComplaints
                      .hasComplaints === true
                  }
                  onChange={() =>
                    setFormData((prevData) => ({
                      ...prevData,
                      guestHealthSafetyCompliance: {
                        ...prevData.guestHealthSafetyCompliance,
                        guestHygieneComplaints: {
                          ...prevData.guestHealthSafetyCompliance
                            .guestHygieneComplaints,
                          hasComplaints: true,
                        },
                      },
                    }))
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "green" }}>Yes</span>
              </label>

              {/* No Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="guestHygieneComplaints"
                  value="false"
                  checked={
                    formData.guestHealthSafetyCompliance.guestHygieneComplaints
                      .hasComplaints === false
                  }
                  onChange={() =>
                    setFormData((prevData) => ({
                      ...prevData,
                      guestHealthSafetyCompliance: {
                        ...prevData.guestHealthSafetyCompliance,
                        guestHygieneComplaints: {
                          ...prevData.guestHealthSafetyCompliance
                            .guestHygieneComplaints,
                          hasComplaints: false,
                        },
                      },
                    }))
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "red" }}>No</span>
              </label>
            </div>
          </div>

          {/* Guest Hygiene Complaints Details */}
          {formData.guestHealthSafetyCompliance.guestHygieneComplaints
            .hasComplaints && (
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Complaint Details</label>
              <input
                type="text"
                name="details"
                placeholder="Enter details of guest hygiene complaints"
                value={
                  formData.guestHealthSafetyCompliance.guestHygieneComplaints
                    .details || ""
                }
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    guestHealthSafetyCompliance: {
                      ...prevData.guestHealthSafetyCompliance,
                      guestHygieneComplaints: {
                        ...prevData.guestHealthSafetyCompliance
                          .guestHygieneComplaints,
                        details: e.target.value,
                      },
                    },
                  }))
                }
                style={inputStyle}
              />
            </div>
          )}
            </>
          )}

          {currentStep === 5 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Staff Exposure & Automation Readiness</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 6 of {totalSteps}
                </p>
              </div>
          {/* Housekeeping Staff Per Shift */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Housekeeping Staff Per Shift</label>
            <input
              type="number"
              name="housekeepingStaffPerShift"
              placeholder="Enter number of housekeeping staff per shift"
              value={
                formData.staffExposureAutomationReadiness
                  .housekeepingStaffPerShift || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "housekeepingStaffPerShift",
                      value,
                    },
                  },
                  "staffExposureAutomationReadiness"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Average Room Cleaning Time */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Average Room Cleaning Time (minutes)
            </label>
            <input
              type="number"
              name="avgRoomCleaningTime"
              placeholder="Enter average room cleaning time"
              value={
                formData.staffExposureAutomationReadiness.avgRoomCleaningTime ||
                ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "avgRoomCleaningTime",
                      value,
                    },
                  },
                  "staffExposureAutomationReadiness"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Staff Disinfection Complaints */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Staff Disinfection Complaints</label>
            <input
              type="number"
              name="staffDisinfectionComplaints"
              placeholder="Enter number of staff disinfection complaints"
              value={
                formData.staffExposureAutomationReadiness
                  .staffDisinfectionComplaints || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "staffDisinfectionComplaints",
                      value,
                    },
                  },
                  "staffExposureAutomationReadiness"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Chemical Exposure Reports */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Chemical Exposure Reports</label>
            <input
              type="number"
              name="chemicalExposureReports"
              placeholder="Enter number of chemical exposure reports"
              value={
                formData.staffExposureAutomationReadiness
                  .chemicalExposureReports || ""
              }
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value)) || 0;
                handleChange(
                  {
                    target: {
                      name: "chemicalExposureReports",
                      value,
                    },
                  },
                  "staffExposureAutomationReadiness"
                );
              }}
              style={inputStyle}
            />
          </div>

          {/* Current PPE Usage */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Current PPE Usage (comma-separated)
            </label>
            <input
              type="text"
              name="currentPPEUsage"
              placeholder="Enter current PPE usage"
              value={
                formData.staffExposureAutomationReadiness.currentPPEUsage.join(
                  ", "
                ) || ""
              }
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  staffExposureAutomationReadiness: {
                    ...prevData.staffExposureAutomationReadiness,
                    currentPPEUsage: e.target.value
                      .split(",")
                      .map((item) => item.trim()),
                  },
                }))
              }
              style={inputStyle}
            />
          </div>

          {/* Automation Readiness (Radio Buttons) */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Automation Readiness</label>
            <div style={{ display: "flex", gap: "15px" }}>
              {/* Yes Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="automationReadiness"
                  value="true"
                  checked={
                    formData.staffExposureAutomationReadiness
                      .automationReadiness === true
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: {
                          name: "automationReadiness",
                          value: true,
                        },
                      },
                      "staffExposureAutomationReadiness"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "green" }}>Yes</span>
              </label>

              {/* No Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="automationReadiness"
                  value="false"
                  checked={
                    formData.staffExposureAutomationReadiness
                      .automationReadiness === false
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: {
                          name: "automationReadiness",
                          value: false,
                        },
                      },
                      "staffExposureAutomationReadiness"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "red" }}>No</span>
              </label>
            </div>
          </div>
            </>
          )}

          {currentStep === 6 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Time & Cost Efficiency of UVC Robot</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 7 of {totalSteps}
                </p>
              </div>
          {[
            {
              label: "Manual Disinfection Time Per Day",
              name: "manualDisinfectionTimePerDay",
              placeholder: "Enter manual disinfection time per day",
            },
            {
              label: "High Risk Area Disinfection Time",
              name: "highRiskAreaDisinfectionTime",
              placeholder: "Enter high risk area disinfection time",
            },
            {
              label: "Chemical Disinfection Dry Time",
              name: "chemicalDisinfectionDryTime",
              placeholder: "Enter chemical disinfection dry time",
            },
            {
              label: "Labour Cost Per Month",
              name: "labourCostPerMonth",
              placeholder: "Enter labour cost per month",
            },
            {
              label: "Disinfection Chemical Cost",
              name: "disinfectionChemicalCost",
              placeholder: "Enter disinfection chemical cost",
            },
            {
              label: "Electricity Consumption",
              name: "electricityConsumption",
              placeholder: "Enter electricity consumption",
            },
            {
              label: "Guest Refund Cost",
              name: "guestRefundCost",
              placeholder: "Enter guest refund cost",
            },
          ].map(({ label, name, placeholder }) => (
            <div style={inputGroupStyle} key={name}>
              <label style={labelStyle}>{label}</label>
              <input
                type="number"
                name={name}
                placeholder={placeholder}
                value={formData.timeCostEfficiencyUVCRobot[name] || ""}
                onChange={(e) => {
                  const value = Math.max(0, Number(e.target.value)) || 0;
                  handleChange(
                    {
                      target: {
                        name,
                        value,
                      },
                    },
                    "timeCostEfficiencyUVCRobot"
                  );
                }}
                style={inputStyle}
              />
            </div>
          ))}
            </>
          )}

          {currentStep === 7 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>AI/UVC Robot Feasibility</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 8 of {totalSteps}
                </p>
              </div>
          {[
            {
              label: "Interest in AI",
              name: "interestInAI",
              type: "radio", // Changed from checkbox to radio
            },
            {
              label: "Estimated Budget",
              name: "estimatedBudget",
              type: "number",
            },
            {
              label: "Financial Model",
              name: "financialModel",
              type: "select",
              options: ["Buy", "Lease", "Subscription"],
            },
            {
              label: "Customization Needs (comma-separated)",
              name: "customizationNeeds",
              type: "text",
            },
            {
              label: "Integration Needs (comma-separated)",
              name: "integrationNeeds",
              type: "text",
            },
            {
              label: "Safety Concerns (comma-separated)",
              name: "safetyConcerns",
              type: "text",
            },
          ].map(({ label, name, type, options }) => (
            <div style={inputGroupStyle} key={name}>
              <label style={labelStyle}>{label}</label>
              {type === "select" ? (
                <select
                  name={name}
                  value={formData.aiUVCRobotFeasibility[name] || ""}
                  onChange={(e) => handleChange(e, "aiUVCRobotFeasibility")}
                  style={inputStyle}
                >
                  <option value="">Select {label}</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : type === "radio" ? (
                <div style={{ display: "flex", gap: "15px" }}>
                  {/* Yes Option */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <input
                      type="radio"
                      name={name}
                      value="true"
                      checked={formData.aiUVCRobotFeasibility[name] === true}
                      onChange={() =>
                        handleChange(
                          { target: { name, value: true } },
                          "aiUVCRobotFeasibility"
                        )
                      }
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontWeight: "bold", color: "green" }}>
                      Yes
                    </span>
                  </label>

                  {/* No Option */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <input
                      type="radio"
                      name={name}
                      value="false"
                      checked={formData.aiUVCRobotFeasibility[name] === false}
                      onChange={() =>
                        handleChange(
                          { target: { name, value: false } },
                          "aiUVCRobotFeasibility"
                        )
                      }
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontWeight: "bold", color: "red" }}>No</span>
                  </label>
                </div>
              ) : (
                <input
                  type={type}
                  name={name}
                  placeholder={
                    type === "number"
                      ? "Enter a number"
                      : "Enter " + label.toLowerCase()
                  }
                  value={
                    [
                      "customizationNeeds",
                      "integrationNeeds",
                      "safetyConcerns",
                    ].includes(name)
                      ? formData.aiUVCRobotFeasibility[name].join(", ")
                      : formData.aiUVCRobotFeasibility[name] || ""
                  }
                  onChange={
                    [
                      "customizationNeeds",
                      "integrationNeeds",
                      "safetyConcerns",
                    ].includes(name)
                      ? (e) =>
                          handleArrayChange(e, "aiUVCRobotFeasibility", name)
                      : (e) => handleChange(e, "aiUVCRobotFeasibility")
                  }
                  style={inputStyle}
                />
              )}
            </div>
          ))}
            </>
          )}

          {currentStep === 8 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Required Demonstration & Next Steps</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 9 of {totalSteps}
                </p>
              </div>
          {/* Onsite Demo Interest */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Interested in Onsite Demo</label>
            <div style={{ display: "flex", gap: "15px" }}>
              {/* Yes Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="onsiteDemoInterest"
                  value="true"
                  checked={
                    formData.requiredDemonstrationNextSteps
                      .onsiteDemoInterest === true
                  }
                  onChange={() =>
                    handleChange(
                      { target: { name: "onsiteDemoInterest", value: true } },
                      "requiredDemonstrationNextSteps"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "green" }}>Yes</span>
              </label>

              {/* No Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="onsiteDemoInterest"
                  value="false"
                  checked={
                    formData.requiredDemonstrationNextSteps
                      .onsiteDemoInterest === false
                  }
                  onChange={() =>
                    handleChange(
                      { target: { name: "onsiteDemoInterest", value: false } },
                      "requiredDemonstrationNextSteps"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "red" }}>No</span>
              </label>
            </div>
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Ready for Pilot Project</label>
            <div style={{ display: "flex", gap: "15px" }}>
              {/* Yes Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="pilotProjectReadiness"
                  value="true"
                  checked={
                    formData.requiredDemonstrationNextSteps
                      .pilotProjectReadiness === true
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: { name: "pilotProjectReadiness", value: true },
                      },
                      "requiredDemonstrationNextSteps"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "green" }}>Yes</span>
              </label>

              {/* No Option */}
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="pilotProjectReadiness"
                  value="false"
                  checked={
                    formData.requiredDemonstrationNextSteps
                      .pilotProjectReadiness === false
                  }
                  onChange={() =>
                    handleChange(
                      {
                        target: { name: "pilotProjectReadiness", value: false },
                      },
                      "requiredDemonstrationNextSteps"
                    )
                  }
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span style={{ fontWeight: "bold", color: "red" }}>No</span>
              </label>
            </div>
          </div>

          {/* Key Decision Maker */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Key Decision Maker</label>
            <input
              type="text"
              name="name"
              placeholder="Enter Name"
              value={
                formData.requiredDemonstrationNextSteps.keyDecisionMaker.name
              }
              onChange={(e) =>
                handleChange(
                  {
                    target: {
                      name: "keyDecisionMaker",
                      value: {
                        ...formData.requiredDemonstrationNextSteps
                          .keyDecisionMaker,
                        name: e.target.value,
                      },
                    },
                  },
                  "requiredDemonstrationNextSteps"
                )
              }
              style={{ ...inputStyle, marginBottom: "10px" }} // Adds spacing between input fields
            />
            <input
              type="text"
              name="contact"
              placeholder="Enter Contact"
              value={
                formData.requiredDemonstrationNextSteps.keyDecisionMaker.contact
              }
              onChange={(e) =>
                handleChange(
                  {
                    target: {
                      name: "keyDecisionMaker",
                      value: {
                        ...formData.requiredDemonstrationNextSteps
                          .keyDecisionMaker,
                        contact: e.target.value,
                      },
                    },
                  },
                  "requiredDemonstrationNextSteps"
                )
              }
              style={inputStyle}
            />
          </div>

          {/* Decision Making Timeline */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Decision Making Timeline</label>
            <input
              type="text"
              name="decisionMakingTimeline"
              placeholder="Enter Timeline (e.g., 1 month)"
              value={
                formData.requiredDemonstrationNextSteps.decisionMakingTimeline
              }
              onChange={(e) =>
                handleChange(e, "requiredDemonstrationNextSteps")
              }
              style={inputStyle}
            />
          </div>
            </>
          )}

          {currentStep === 9 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h3 style={sectionTitleStyle}>Additional Notes</h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Step 10 of {totalSteps}
                </p>
              </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Notes</label>
            <textarea
              name="note"
              value={formData.additionalnotes.note}
              onChange={(e) => handleChange(e, "additionalnotes")}
              style={{
                width: "100%",
                minHeight: 120,
                padding: "0.65rem 0.85rem",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: "1rem",
                outline: "none",
                boxSizing: "border-box",
                resize: "vertical",
              }}
              placeholder="Enter any additional notes here..."
            />
          </div>
            </>
          )}
        </div>

        <div
          style={{
            maxWidth: 720,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent:
              currentStep === 0 ? "flex-end" : "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {currentStep > 0 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => s - 1)}
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                color: "#64748b",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              Previous
            </button>
          ) : (
            <span />
          )}
          {currentStep < totalSteps - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => s + 1)}
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              style={{
                padding: "0.65rem 1.25rem",
                fontSize: "1rem",
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
              }}
            >
              Submit
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default StarHotel;
